import { useQuery } from "@tanstack/react-query";
import { Modal } from "../Modal.js";
import { tenantProfessionsApi } from "../../api/tenantProfessionsApi.js";
import { useIssuerMasterMutations } from "../../hooks/useIssuerMaster.js";
import { useDocTypeProfession } from "../../hooks/useDocTypeProfession.js";
import { IssuerValuesForm } from "../work-item-master/IssuerValuesForm.js";
import { ProfessionHolderRoster } from "../work-item-master/ProfessionHolderRoster.js";
import type { IssuerRef } from "../../lib/issuerMaster.js";

/**
 * テンプレート編集中に、そのテンプレートが参照する発行者情報（業務マスタ）と
 * 資格者名簿を、その場で登録・編集するためのダイアログ。
 * ここで登録した資格者は同じ資格種別のすべてのテンプレート・書類で共有される
 * （docs/design/user-professional-separation.md §8.4）。
 */
export function IssuerMasterDialog({
  docType,
  referencedKeys,
  onClose,
}: {
  docType: string;
  referencedKeys: IssuerRef[];
  onClose: () => void;
}) {
  const { profession: professionKey } = useDocTypeProfession(docType);
  const professionsQuery = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
  });
  const { saveMut, deleteRuleMut } = useIssuerMasterMutations();

  const profession = professionsQuery.data?.data.find((entry) => entry.profession === professionKey) ?? null;

  return (
    <Modal title="発行者情報の登録" onClose={onClose} width={680}>
      {professionsQuery.isLoading ? (
        <div style={{ padding: 20, color: "#64748b" }}>読み込み中...</div>
      ) : !profession ? (
        <div style={{ padding: 20, color: "#64748b" }}>この書類種別には発行者情報がありません。</div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>
            このテンプレートが参照する発行者情報です。ここで保存した値は業務マスタに登録され、
            同じ職能のすべての書類・テンプレートで共有されます。
          </div>
          <IssuerValuesForm
            profession={profession}
            saving={saveMut.isPending || deleteRuleMut.isPending}
            referencedKeys={referencedKeys}
            onSave={(input) => saveMut.mutateAsync(input)}
            onDeleteRule={(rule) => deleteRuleMut.mutate({ profession: profession.profession, ruleId: rule.id })}
            roster={<ProfessionHolderRoster profession={profession.profession} />}
          />
        </div>
      )}
    </Modal>
  );
}
